import { Grid } from "@mui/system";
import { useNavigate } from "react-router";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { Box, Button, FormControl, Typography } from "@mui/material";
import { useForm, UseFormGetValues, UseFormReset, useWatch } from "react-hook-form";
import dayjs from "dayjs";

import {
  ButtonMinWidth,
  FaCheckCircleIcon,
  FaExclamationCircleIcon,
  HeaderTitle,
  width100,
} from "../styles/style";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { EligibilityCheckResponse } from "../../../models/api/Patient";
import {
  BenefitPlanDetails,
  CheckEligibility,
  EligibilityCheck,
  GetPatientInformationPayload,
  InfoAddress,
  LogContractIdPbpPayload,
  MemberInfoData,
  Patient,
  PatientInformation,
  PatientInformationView,
  SaveInformation,
} from "../../../models/Patient";
import {
  flexAllEnd,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import ReadOnlyField from "../../../components/FormPriorAuthReAuth/ReadOnlyField";
import {
  formatMailingAddress,
  formatResidentialAddress,
  getConcatStringOrNull,
  getFormattedPhoneNumber,
  getGender,
  getLanguage,
  getLanguageLabel,
  getLanguageValue,
  getList,
  getValidMBI,
  getValue,
  getZipCode,
  isEligible,
  isMemberEligible,
  mapUserDefinedFields,
  validatePhoneNumber,
} from "../../../utils";
import {
  formatDate,
  formatDateWithoutConversionTimezone,
} from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { FormInputDropdown } from "../../../components/FormPriorAuthReAuth/FormInputDropdown";
import { rules } from "../../../utils/validation/Validation";
import { GenderType, getPayloadForInformationPriorAuth, TextConstants } from "../../../constants/Patient";
import { ApplicationRoute } from "../../../constants/ApplicationUrl";
import {
  Constants,
  Numbers,
} from "../../../constants/Referral";
import { MyAccountModel } from "../../../models/MyAccount";
import { PhoneFaxExtInput } from "../../../components/FormPriorAuthReAuth/PhoneFaxExtInput";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { extractPatientAddress, initMapScript } from "../../../utils/Address";
import { defaultMemberInfoData } from "../../../reducers/MemberInfoData";
import { fontSize } from "../../../styles/mui/styles/font";
import { Language } from "../../../models/MemberDetails";

type EligibilityCheckSuccessType = {
  eligibilityResponse: EligibilityCheckResponse | null | undefined;
  eligibilityPayload: EligibilityCheck | null | undefined;
  patientFromMbiState: Patient;
  benefitPlanUidState: BenefitPlanDetails;
  loggedInUserState: MyAccountModel;
  patientInformationByIdState: PatientInformation;
  memberInfoDataState: MemberInfoData;
  informationState: PatientInformationView;
  createPatient: (payload: FormData) => void;
  createPatientState: PatientInformationView;
  getPatientInformationById: (payload: GetPatientInformationPayload) => void;
  information: (payload: SaveInformation) => void;
  saveMemberInfo: (payload: MemberInfoData) => void;
  createPatientErrorState: any;
  informationErrorState: any;
  resetAPIResponse: () => void;
  updateLogContractIdPbp: (payload: LogContractIdPbpPayload) => void;
  logContractIdPBPDetailsState: boolean;
  masterLanguageState: Language[];
};

const memberInfoWatchFields = [
          "gender",
          "phoneNumber",
          "memberPreferredLanguage",
          "street1",
          "street2",
          "county",
          "city",
          "patientState",
          "zipcode",
          "country",
        ];

const getResidentialAddress = (getValues:UseFormGetValues<any>) => {
  return formatResidentialAddress({
  addressLine: getValues().street1,
  addressLine2: getValues().street2,
  addressType: "Residential",
  city: getValues().city,
  state: getValues().patientState,
  zipCode: getValues().zipcode,
  countryCode: getValues().country,
  county: getValues().county,
});
};

const updateMemberInfo = (
  newData: Partial<MemberInfoData>,
  setMemberInfoData: React.Dispatch<React.SetStateAction<MemberInfoData>>
) => {
  setMemberInfoData((prevData) => ({
    ...prevData,
    ...newData,
  }));
};

const onChangeAddress = (
  autocomplete: any,
  setMemberInfoData: React.Dispatch<React.SetStateAction<MemberInfoData>>,
  getValues: UseFormGetValues<any>,
  reset: UseFormReset<any>
) => {
  const place = autocomplete.getPlace();
  const address1 = extractPatientAddress(place);
  reset({
    ...getValues(),
    street1: getConcatStringOrNull(address1.streetNumber, address1.streetName1),
    street2: getConcatStringOrNull(address1.streetName2),
    city: getConcatStringOrNull(address1.city),
    patientState: getConcatStringOrNull(address1.patientState),
    zipcode: getZipCode(getConcatStringOrNull(address1.zipCode)),
    country: address1.country,
    county: address1.county,
  });
  updateMemberInfo(
    {
      streetName1: getConcatStringOrNull(
        address1.streetNumber,
        address1.streetName1
      ),
      streetName2: getConcatStringOrNull(address1.streetName2),
      city: getConcatStringOrNull(address1.city),
      patientState: getConcatStringOrNull(address1.patientState),
      zipCode: getZipCode(getConcatStringOrNull(address1.zipCode)),
      county: address1.county,
      residentialAddress: getResidentialAddress(getValues),
    },
    setMemberInfoData
  );
};

const initAutocomplete = (
  searchInputMember: MutableRefObject<HTMLInputElement>,
  setMemberInfoData: React.Dispatch<React.SetStateAction<MemberInfoData>>,
  getValues: UseFormGetValues<any>,
  reset: UseFormReset<any>
) => {
  if (!searchInputMember.current) {
    return;
  } else {
    const autocomplete = new window.google.maps.places.Autocomplete(
      searchInputMember.current
    );
    autocomplete.setFields(["address_component", "geometry"]);
    autocomplete.addListener("place_changed", () =>
      onChangeAddress(autocomplete, setMemberInfoData, getValues, reset)
    );
  }
};

export const EligibilityCheckSuccess = ({
  eligibilityResponse,
  eligibilityPayload,
  patientFromMbiState,
  benefitPlanUidState,
  loggedInUserState,
  patientInformationByIdState,
  createPatient,
  createPatientState,
  getPatientInformationById,
  information,
  saveMemberInfo,
  memberInfoDataState,
  informationState,
  createPatientErrorState,
  informationErrorState,
  updateLogContractIdPbp,
  masterLanguageState
}: EligibilityCheckSuccessType) => {
  const methods = useForm<any>({
    defaultValues: "",
    mode: "all",
  });

  const { control, setValue, handleSubmit, getValues, reset } = methods;
  // const [isPatientCreation, setIsPatientCreation] = useState<boolean>(false);//TBD for ingest
  const [disableLanguage, setDisableLanguage] = useState<boolean>(false);
  const [patientEligibilityStatus, setPatientEligibilityStatus] =
    useState<string>("");
  const [eligibilityResidentialAddress, setEligibilityResidentialAddress] =
    useState<InfoAddress | undefined | null>();
  const [eligibilityMailingAddress, setEligibilityMailingAddress] = useState<
    InfoAddress | undefined | null
  >();
  const [isMailingOnlyAddress, setIsMailingOnlyAddress] = useState<boolean>(false);
  const [memberInfoData, setMemberInfoData] = useState<MemberInfoData>(
    defaultMemberInfoData
  );

  const searchInputMember = useRef() as MutableRefObject<HTMLInputElement>;
  const watchedGender = useWatch({ control, name: "gender" });
  const watchedMemberPreferredLanguage = useWatch({
    control,
    name: "memberPreferredLanguage",
  });
  const watchedPhone = useWatch({ control, name: "phoneNumber" });

  useEffect(() => {
    updateMemberInfo({
      gender: watchedGender,
      memberPreferredLanguage: watchedMemberPreferredLanguage,
      phoneNumber:
        validatePhoneNumber(memberInfoData.phoneNumber) &&
        getValue(
          getValue(eligibilityResponse, "response"),
          "supplementalInformation.info[0].homePhone", ""
        ).length > 0
          ? memberInfoData.phoneNumber
          : watchedPhone,
    },
    setMemberInfoData
  );
    // eslint-disable-next-line
  }, [watchedGender, watchedMemberPreferredLanguage, watchedPhone]);

  useEffect(() => {
    updateMemberInfo({
      isIngest:
        informationErrorState?.data?.status === 400 ||
        createPatientErrorState?.data?.status === 400
          ? true
          : memberInfoData.isIngest,
    },
    setMemberInfoData);
    // eslint-disable-next-line
  }, [informationErrorState, createPatientErrorState]);

  useEffect(() => {
    initMapScript().then(() => initAutocomplete(searchInputMember ,setMemberInfoData , getValues, reset));
    // eslint-disable-next-line
  }, [searchInputMember.current]);

  const navigate = useNavigate();
  const callPatientInformationById = (patientId: number) => {
    const payload = {
      id: patientId,
      type: 1,
    };
    getPatientInformationById(payload);
  };
  useEffect(() => {
    if (
      !isEmpty(patientFromMbiState?.patientDetails) &&
      patientFromMbiState?.patientDetails.length > Numbers.ZERO &&
      !isEmpty(benefitPlanUidState) &&
      getValue(benefitPlanUidState, "benefitPlanUid", "").length > Numbers.ZERO &&
      patientFromMbiState?.proceedWithIngest === null
    ) {
      callPostInformation();
    }
  }, [patientFromMbiState, benefitPlanUidState]);

  useEffect(() => {
    if (!isEmpty(benefitPlanUidState) &&
      getValue(benefitPlanUidState, "benefitPlanUid", "").length > Numbers.ZERO &&
      patientFromMbiState?.proceedWithIngest === false) {
      createNewPatient();
    }
  }, [patientFromMbiState, benefitPlanUidState]);

  useEffect(() => {
    if (createPatientState && createPatientState.patientId > 0) {
      callPatientInformationById(createPatientState.patientId);
    }
  }, [createPatientState]);

  useEffect(() => {
    if (informationState && informationState.patientId > Numbers.ZERO && patientInformationByIdState
      && patientInformationByIdState.patientId === informationState.patientId && 
    patientInformationByIdState.patientEligibilityId !== informationState.patientEligibilityId) {
      callPatientInformationById(informationState.patientId);
    }
  }, [informationState]);

  useEffect(() => {
    reset({
      gender: null,
      phoneNumber: "",
      memberPreferredLanguage: null,
    });
    setEligibilityResidentialAddress(null);
    setEligibilityMailingAddress(null);
    setMemberInfoData(defaultMemberInfoData);
    setFormValues();
  }, [getValue(eligibilityResponse, "response")]);

  const watchedFields = useWatch({
    control,
    name: memberInfoWatchFields,
  });
  const [isFlagSet, setFlag] = useState(false);

  useEffect(() => {
    if (
      watchedFields &&
      watchedFields.some(
        (field, idx) => field !== getValues()[memberInfoWatchFields[idx]]
      )
    ) {
      setFlag(true);
    }
  }, [watchedFields, getValues]);
  
  const subscriberDetails = () =>
    mapUserDefinedFields({
      healthPlanUid: getValue(
        eligibilityResponse,
        "response.additionalNotes.healthPlanUid"
      ),
      UserDefined7: getValue(
        eligibilityResponse,
        "response.supplementalInformation.info[0].userDefined7"
      ),
      UserDefined6: getValue(
        eligibilityResponse,
        "response.supplementalInformation.info[0].userDefined6"
      ),
    });
  
  const callPostInformation = (type?: number) => {
    const payload = getPayloadForInformationPriorAuth({
      checkEligibilityState: getValue(eligibilityResponse, "response"),
      patientEligibilityStatus: patientEligibilityStatus,
      eligibilityPayload: eligibilityPayload,
      patientFromMbiState: patientFromMbiState,
      reAuthDefaultValueState: null,
      benefitPlanUidState: benefitPlanUidState,
      patientInformationByIdState: patientInformationByIdState,
    });

    if (
      !isEmpty(payload.informationReceived) &&
      payload.informationReceived.patientId > 0
    ) {
      const updatedPayload = {
        ...payload,
        informationReceived: {
          ...payload.informationReceived,
          gender: getGenderValue(),
          phoneNo: memberInfoData.phoneNumber,
          languagePreferenceValue:
            getLanguage(
              getValue(
                eligibilityResponse,
                "response.supplementalInformation.info[0].userDefined1",
              ),
            ) ?? "",
          subscriberSuffix: subscriberDetails().subscriberSuffix,
          groupPlanId: subscriberDetails().groupPlanId,
          subscriberPrefix: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined8", ""),
          relationshipTypeId: subscriberDetails().relationshipTypeId,
          subscriberFirstName: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined9", ""),
          subscriberLastName: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined10", ""),
          subscriberDateOfBirth: formatDate(
              getValue(
                eligibilityResponse,
                "response.supplementalInformation.info[0].userDefined11",
              ),
              DateFormats.YYYY_MM_DD,
              DateFormats.YYYYMMDD
            ) || null,
          subscriberGender: getGender(getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined12", "")),
        },
      };

      if (type && type === 1) { 
        const hasEligibilityMailingAddress = getValue(eligibilityResponse, "response.supplementalInformation.info[0].addresses", []).some((item: any) => {
          const addressType = getValue(item, "addressType", "").toLowerCase();
          const hasAddressData =
            !isEmpty(getValue(item, "addressLine")) ||
            !isEmpty(getValue(item, "addressLine2")) ||
            !isEmpty(getValue(item, "city")) ||
            !isEmpty(getValue(item, "state")) ||
            !isEmpty(getValue(item, "zipCode")) ||
            !isEmpty(getValue(item, "countryCode")) ||
            !isEmpty(getValue(item, "county"));
            
          return addressType === "mailing" && hasAddressData;
        });

        const finalPayload = {
          ...updatedPayload,
          informationReceived: {
            ...updatedPayload.informationReceived,
            gender: getGender(getValues().gender),
            phoneNo: memberInfoData.phoneNumber,
            languagePreferenceValue: getLanguageLabel(
              getValues().memberPreferredLanguage ?? 1
            ),
            subscriberSuffix: subscriberDetails().subscriberSuffix,
            groupPlanId: subscriberDetails().groupPlanId,
            subscriberPrefix: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined8", ""),
            relationshipTypeId: subscriberDetails().relationshipTypeId,
            subscriberFirstName: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined9", ""),
            subscriberLastName: getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined10", ""),
            subscriberDateOfBirth: formatDate(
              getValue(
                eligibilityResponse,
                "response.supplementalInformation.info[0].userDefined11",
              ),
              DateFormats.YYYY_MM_DD,
              DateFormats.YYYYMMDD
            ) || null,
            subscriberGender: getGender(getValue(eligibilityResponse, "response.supplementalInformation.info[0].userDefined12", "")),
          },
          patientMailingAddress: hasEligibilityMailingAddress
            ? updatedPayload.patientMailingAddress
            : {
                ...updatedPayload.patientMailingAddress,
                streetName1: getValues().street1,
                streetName2: getValues().street2,
                county: getValues().county,
                city: getValues().city,
                patientState: getValues().patientState,
                zipCode: getValues().zipcode,
                country: getValues().country,
              },
          patientResidentialAddress: {
            ...updatedPayload.patientResidentialAddress,
            streetName1: getValues().street1,
            streetName2: getValues().street2,
            county: getValues().county,
            city: getValues().city,
            patientState: getValues().patientState,
            zipCode: getValues().zipcode,
          },
        };
        information(finalPayload);
      } else {
        information(updatedPayload);
      }
    }
  };
  const getGenderValue = () => {
    let genderValue;
    if (isEligible(getValue(eligibilityResponse, "response"))) {
      genderValue = getValue(
        getValue(eligibilityResponse, "response"),
        "record.subscriberGender",
        ""
      );
    } else {
      genderValue = getValue(eligibilityResponse, "response").record.subscriberGender;
    }
    return getGender(genderValue);
  };
  const address = (checkEligibilityState: CheckEligibility) => {
    const addresses = getValue(checkEligibilityState, "supplementalInformation.info[0].addresses", []) as InfoAddress[];
    const residentialAddress = addresses.find(
      (item: any) =>
        getValue(item, "addressType", "").toLowerCase() === "residential"
    );
    const mailingAddress = addresses.find(
      (item: any) =>
        getValue(item, "addressType", "").toLowerCase() === "mailing"
    );

    const mailingOnly = !residentialAddress && !!mailingAddress;
    setIsMailingOnlyAddress(mailingOnly);

    if (residentialAddress) {
      setEligibilityResidentialAddress(residentialAddress);
      setEligibilityMailingAddress(mailingAddress ?? null);
    } else if (mailingAddress) {
      setEligibilityResidentialAddress({
        ...mailingAddress,
        addressType: "residential",
      });
      setEligibilityMailingAddress(mailingAddress);
    } else {
      setEligibilityResidentialAddress(null);
      setEligibilityMailingAddress(null);
    }
  };
  const createNewPatient = () => {
    const response = getValue(eligibilityResponse, "response");
    const subscriberName = getValue(response, "record.subscriberName", {});
    const homePhone = getValue(getValue(response, "supplementalInformation.info[0]"), "homePhone", "");
    const gender = isEligible(response)
      ? getValue(response, "record.subscriberGender", "")
      : response?.record?.subscriberGender;
      const payload = getPayloadForInformationPriorAuth({
        checkEligibilityState: response,
        patientEligibilityStatus: isMemberEligible(response)
          ? "Eligible"
          : "Not Eligible",
        eligibilityPayload: eligibilityPayload,
        patientFromMbiState: patientFromMbiState,
        reAuthDefaultValueState: null,
        benefitPlanUidState: benefitPlanUidState,
        patientInformationByIdState: patientInformationByIdState,
      });
    setPatientEligibilityStatus(isMemberEligible(response) ? "Eligible" : "Not Eligible");

    const formData = new FormData();
    // Patient
    formData.append("createPatient.CreatePatient.PatientId", "0");
    formData.append("createPatient.CreatePatient.MiddleName", "");
    formData.append("createPatient.CreatePatient.LastName", subscriberName.last || "");
    formData.append("createPatient.CreatePatient.UserCreated", Constants.SYSTEM_USER);
    formData.append("createPatient.CreatePatient.Gender", getGender(gender));
    formData.append("createPatient.CreatePatient.CellPhone", "");
    formData.append(
      "createPatient.CreatePatient.FullNameCreated",
      `${subscriberName.first || ""} ${subscriberName.last || ""}`
    );
    formData.append("createPatient.CreatePatient.LanguagePreferenceKind", "");
    formData.append("createPatient.CreatePatient.PhoneNo", getFormattedPhoneNumber(homePhone, getValues()));
    formData.append(
      "createPatient.CreatePatient.DateOfBirth",
      formatDateWithoutConversionTimezone(
        getValue(eligibilityPayload, "dob"),
        DateFormats.YYYY_MM_DD
      )
    );
    formData.append("createPatient.CreatePatient.FirstName", subscriberName.first || "");
    formData.append(
      "createPatient.CreatePatient.LanguagePreferenceValue",
      getValue(payload, "informationReceived.languagePreferenceValue") ?? ""
    );
    formData.append("createPatient.CreatePatient.Extension", "");
    formData.append("createPatient.CreatePatient.IsManual", "False");
    formData.append("createPatient.CreatePatient.EmailAddress", "");
    formData.append("createPatient.CreatePatient.BlobId", "0");

    // InformationReceived
    const info = getValue(payload, "informationReceived", {});
    formData.append("createPatient.InformationReceived.SubscriberId", info.subscriberId || "");
    formData.append("createPatient.InformationReceived.EligibilityPayerId", info.eligibilityPayerId || "");
    formData.append("createPatient.InformationReceived.HealthPlanUid", info.healthPlanUid || "");
    formData.append(
      "createPatient.InformationReceived.FullNameCreated",
      getValue(loggedInUserState, "providerPortalUser.email") || ""
    );
    formData.append("createPatient.InformationReceived.Product", info.product || "");
    formData.append("createPatient.InformationReceived.ApiEligibilityId", info.apiEligibilityId || "");
    formData.append("createPatient.InformationReceived.UserCreated", Constants.SYSTEM_USER);
    formData.append("createPatient.InformationReceived.EffectiveDate", info.effectiveDate || "");
    formData.append("createPatient.InformationReceived.EligibilityPayerName", info.eligibilityPayerName || "");
    formData.append("createPatient.InformationReceived.BenefitPlanUid", info.benefitPlanUid || "");
    formData.append("createPatient.InformationReceived.EffectiveThrough", info.effectiveThrough || "");
    formData.append("createPatient.InformationReceived.EligibilityStatus", info.eligibilityStatus || "");
    formData.append("createPatient.InformationReceived.contractIdPbp", info.contractIdPbp || "");
    formData.append("createPatient.InformationReceived.PatientId", "0");
    formData.append("createPatient.InformationReceived.AhcccsId", "");
    formData.append("createPatient.InformationReceived.AlternatePhone", "");
    formData.append("createPatient.InformationReceived.accommodationName", info.accommodationName || "");
    formData.append(
      "createPatient.InformationReceived.MedicareBeneficiaryIdentifier",
      info.medicareBeneficiaryIdentifier ?? ""
    );
    formData.append("createPatient.InformationReceived.IsManual", "false");
    formData.append("createPatient.InformationReceived.SubscriberSuffix", info.subscriberSuffix || "");
    formData.append("createPatient.InformationReceived.GroupPlanId", info.groupPlanId || "");
    formData.append("createPatient.InformationReceived.SubscriberPrefix", info.subscriberPrefix || "");
    formData.append("createPatient.InformationReceived.RelationshipTypeId", info.relationshipTypeId || "");
    formData.append("createPatient.InformationReceived.SubscriberFirstName", info.subscriberFirstName || "");
    formData.append("createPatient.InformationReceived.SubscriberLastName", info.subscriberLastName || "");
    formData.append("createPatient.InformationReceived.SubscriberDateOfBirth", info.subscriberDateOfBirth || "");
    formData.append("createPatient.InformationReceived.SubscriberGender", info.subscriberGender || "");

    // Mailing Address
    const mailing = getValue(payload, "patientMailingAddress", {});
    formData.append("createPatient.PatientMailingAddress.Country", mailing.country || "");
    formData.append("createPatient.PatientMailingAddress.AddressTypeId", "3");
    formData.append("createPatient.PatientMailingAddress.ZipCode", mailing.zipCode || "");
    formData.append("createPatient.PatientMailingAddress.PatientState", mailing.patientState || "");
    formData.append("createPatient.PatientMailingAddress.City", mailing.city || "");
    formData.append("createPatient.PatientMailingAddress.StreetName1", mailing.streetName1 || "");
    formData.append("createPatient.PatientMailingAddress.County", mailing.county || "");
    formData.append("createPatient.PatientMailingAddress.StreetName2", mailing.streetName2 || "");
    formData.append("createPatient.PatientMailingAddress.Latitude", "");
    formData.append("createPatient.PatientMailingAddress.Longitude", "");

    // Residential Address
    const residential = getValue(payload, "patientResidentialAddress", {});
    formData.append("createPatient.PatientResidentialAddress.StreetName1", residential.streetName1 || "");
    formData.append("createPatient.PatientResidentialAddress.StreetName2", residential.streetName2 || "");
    formData.append("createPatient.PatientResidentialAddress.Latitude", "");
    formData.append("createPatient.PatientResidentialAddress.Longitude", "");
    formData.append("createPatient.PatientResidentialAddress.City", residential.city || "");
    formData.append("createPatient.PatientResidentialAddress.PatientState", residential.patientState || "");
    formData.append("createPatient.PatientResidentialAddress.ZipCode", residential.zipCode || "");
    formData.append("createPatient.PatientResidentialAddress.County", residential.county || "");
    formData.append("createPatient.PatientResidentialAddress.AddressTypeId", "1");
    formData.append("createPatient.PatientResidentialAddress.Country", residential.country || "");

    createPatient(formData);
  };
  const setFormValues = () => {
    /**Member details After eligibility check */
    address(getValue(eligibilityResponse, "response"));

    setValue(
      "gender",
      isEligible(getValue(eligibilityResponse, "response"))
        ? getValue(
            getValue(eligibilityResponse, "response"),
            "record.subscriberGender",
            ""
          )
        : getValue(eligibilityResponse, "response").record.subscriberGender
    );
    setValue("subscriberId", getValue(eligibilityPayload, "policyId"));
    setValue(
      "healthPlan",
      !isEmpty(getValue(eligibilityPayload, "payerName"))
        ? getValue(eligibilityPayload, "payerName").toUpperCase()
        : ""
    );
    setPatientEligibilityStatus(
      isMemberEligible(getValue(eligibilityResponse, "response"))
        ? "Eligible"
        : "Not Eligible"
    );
    setValue(
      "memberPreferredLanguage",
      getLanguageValue(getLanguage(
        getValue(eligibilityResponse, "response")?.supplementalInformation
          ?.info[0]?.userDefined1)
      )
    );
    checkLanguageAvailable(
      getValue(eligibilityResponse, "response"),
      patientInformationByIdState
    );
  };

  const checkLanguageAvailable = (
    checkEligibilityData: CheckEligibility,
    patientInfo: PatientInformation
  ) => {
    const eligibilityLanguage = getLanguageLabel(
      getLanguage(
        getValue(
          checkEligibilityData,
          "supplementalInformation.info[0].userDefined1")
      )
    );
    const patientLanguage = getValue(patientInfo, "memberInfo.languageId");
    setDisableLanguage(
      (eligibilityLanguage && Number(eligibilityLanguage) > 0) ||
        !isEmpty(patientLanguage)
    );
  };
  const setValuesBaseOnAddress = (type?: number) => {
    const address = getValue(eligibilityResponse, "response");
    const isResidential = eligibilityResidentialAddress !== null;
    const isMailing = !isResidential && eligibilityMailingAddress !== null;

    if (isResidential || isMailing) {
      const isElig = isEligible(address);
      const memberData = {
        dob: dayjs(getValue(eligibilityPayload, "dob")).format(
          DateFormats.MM_DD_YYYY
        ),
        gender: isResidential
          ? getValues().gender
          : getValue(eligibilityResponse, "response.record.subscriberGender"),
        payerId: getValue(eligibilityPayload, "payerId"),
        mbi: !isElig
          ? ""
          : getValidMBI(address.supplementalInformation.info[0].mbi),
        subscriberId: getValue(eligibilityPayload, "policyId"),
        lastName: getValue(eligibilityPayload, "lastName"),
        healthPlan: getValue(eligibilityPayload, "payerName").toUpperCase(),
        streetName1: !isElig
          ? ""
          : isResidential
          ? getValue(eligibilityResidentialAddress, "addressLine")
          : getValue(eligibilityMailingAddress, "street") ||
            getValue(eligibilityMailingAddress, "addressLine"),
        streetName2: !isElig
          ? ""
          : isResidential
          ? getValue(eligibilityResidentialAddress, "addressLine2")
          : getValue(eligibilityMailingAddress, "addressLine2", ""),
        city: !isElig
          ? ""
          : isResidential
          ? getValue(eligibilityResidentialAddress, "city")
          : getValue(eligibilityMailingAddress, "city"),
        patientState: !isElig
          ? ""
          : isResidential
          ? getValue(eligibilityResidentialAddress, "state")
          : getValue(eligibilityMailingAddress, "state"),
        zipCode: !isElig
          ? ""
          : isResidential
          ? getZipCode(getValue(eligibilityResidentialAddress, "zipCode"))
          : getZipCode(
              getValue(eligibilityMailingAddress, "zip") ||
                getValue(eligibilityMailingAddress, "zipCode")
            ),
        country: !isElig
          ? "USA"
          : isResidential
          ? getValue(eligibilityResidentialAddress, "countryCode")
          : getValue(eligibilityMailingAddress, "country") ||
            getValue(eligibilityMailingAddress, "countryCode"),
        county: !isElig
          ? ""
          : isResidential
          ? getValue(eligibilityResidentialAddress, "county")
          : getValue(eligibilityMailingAddress, "county"),
        phoneNumber: getFormattedPhoneNumber(
          getValue(address, "supplementalInformation.info[0].homePhone"),
          getValues()
        ),
        memberPreferredLanguage: getValues().memberPreferredLanguage,
        memberName: `${getValue(
          eligibilityResponse,
          "response.record.subscriberName.first"
        )} ${getValue(
          eligibilityResponse,
          "response.record.subscriberName.last"
        )}`,
        residentialAddress: formatResidentialAddress(
          eligibilityResidentialAddress
        ),
        mailingAddress: formatMailingAddress(
          eligibilityMailingAddress ?? (eligibilityResidentialAddress
              ? {
                  ...eligibilityResidentialAddress,
                  addressType: "Mailing",
                }
              : null)
        ),
        residentialAddressObj: eligibilityResidentialAddress,
        mailingAddressObj: eligibilityMailingAddress ??
          (eligibilityResidentialAddress
            ? {
                ...eligibilityResidentialAddress,
                addressType: "Mailing",
              }
            : null),
        isIngest:
          !isElig ||
          getValue(patientFromMbiState, "proceedWithIngest") === true ||
          getValue(createPatientErrorState, "status") === 400 ||
          getValue(informationErrorState, "status") === 400,
        isPatientCreation:
          getValue(patientFromMbiState, "proceedWithIngest") === false,
        groupId: getValue(address, "supplementalInformation.info[0].userDefined7"),
      };

      setMemberInfoData(memberData);

      if (type !== 1) {
        reset(() => ({
          ...getValues(),
          street1: memberData.streetName1,
          street2: memberData.streetName2,
          city: memberData.city,
          patientState: memberData.patientState,
          zipcode: memberData.zipCode,
          country: memberData.country,
          county: memberData.county,
          residentialAddress: getResidentialAddress(getValues),
          mailingAddress: memberData.mailingAddress,
        }));
      }
    }

    if (type === 1) {
      setTimeout(() => {
        handleSubmit(() => onSubmit(type))();
      }, 500);
    }
  };
  const [
    watchedStreet1,
    watchedStreet2,
    watchedCity,
    watchedPatientState,
    watchedZipcode,
    watchedCounty,
    watchedCountry,
  ] = useWatch({
    control,
    name: [
      "street1",
      "street2",
      "city",
      "patientState",
      "zipcode",
      "county",
      "country",
    ],
  });

  useEffect(() => {
    const hasResidentialAddress = eligibilityResidentialAddress !== null;
    const hasMailingAddress = eligibilityMailingAddress !== null;
    const currentResidentialAddressObj: InfoAddress = {
      addressLine: watchedStreet1 ?? "",
      addressLine2: watchedStreet2 ?? "",
      addressType: "residential",
      city: watchedCity ?? "",
      state: watchedPatientState ?? "",
      zipCode: watchedZipcode ?? "",
      countryCode: watchedCountry ?? "",
      county: watchedCounty ?? "",
    };
    const currentMailingAddressObj: InfoAddress = {
      ...currentResidentialAddressObj,
      addressType: "mailing",
    };

    const memberInfoUpdates: Partial<MemberInfoData> = {
      streetName1: watchedStreet1,
      streetName2: watchedStreet2,
      city: watchedCity,
      patientState: watchedPatientState,
      zipCode: watchedZipcode,
      county: watchedCounty,
      country: watchedCountry,
    };

    if (isMailingOnlyAddress || hasResidentialAddress) {
      memberInfoUpdates.residentialAddress = formatResidentialAddress(
        currentResidentialAddressObj
      );
      memberInfoUpdates.residentialAddressObj = currentResidentialAddressObj;

      if (!hasMailingAddress) {
        memberInfoUpdates.mailingAddress = formatMailingAddress(
          currentMailingAddressObj
        );
        memberInfoUpdates.mailingAddressObj = currentMailingAddressObj;
      }
    } else if (hasMailingAddress) {
      memberInfoUpdates.mailingAddress = formatMailingAddress(
        currentMailingAddressObj
      );
      memberInfoUpdates.mailingAddressObj = currentMailingAddressObj;
    }

    updateMemberInfo(memberInfoUpdates, setMemberInfoData);
    // eslint-disable-next-line
  }, [
    watchedStreet1,
    watchedStreet2,
    watchedCity,
    watchedPatientState,
    watchedZipcode,
    watchedCounty,
    watchedCountry,
  ]);

  useEffect(() => {
    setValuesBaseOnAddress();
  }, [eligibilityMailingAddress, eligibilityResidentialAddress]);

  useEffect(() => {
    if (
      memberInfoDataState &&
      !isEmpty(getValue(memberInfoDataState, "memberName")) 
    ) {
      navigate(ApplicationRoute.PRIOR_AUTH_WORKFLOW);
    }
  }, [memberInfoDataState]);

  useEffect(() => {
    if (informationState && informationState.patientId > Numbers.ZERO) {
      callPatientInformationById(informationState.patientId);
    }
  }, [informationState]);

  const submit = (type: number) => {
    setValuesBaseOnAddress(type);
  };
  const onSubmit = (type: number) => {
    
    if (isFlagSet) {
      callPostInformation(type);
    }
    saveMemberInfo(memberInfoData);
  };

  useEffect(() => {
    const currentDateTime = new Date().toISOString();
    if (
      patientInformationByIdState &&
      getValue(patientInformationByIdState, "patientId") > 0 &&
      getValue(patientInformationByIdState, "isAuthNotRequired")
    ) {
      updateLogContractIdPbp({
        userId: loggedInUserState.providerPortalUser.userId,
        timestamp: currentDateTime,
        memberId: getValue(eligibilityResponse, "response.additionalNotes.apiRequestId"),
        contractId: getValue(patientInformationByIdState, "memberInfo.contractIdPbp"),
        userCreated: loggedInUserState.providerPortalUser.email,
        dateCreated: currentDateTime,
        userUpdated: "system",
        dateUpdated: currentDateTime,
      } as LogContractIdPbpPayload);
    }
  }, [patientInformationByIdState]);

  return (
    <>
      <Grid size={12} m={2}>
        <Grid container>
          <Grid size={12} sx={{ display: "flex" }}>
            <FaCheckCircleIcon />
            <Box sx={HeaderTitle}>
                <Typography
                variant="body1"
                color={colors.fonts[20]}
                fontWeight={fontWeight[4]}
                sx={{ fontSize: fontSize[12]}}
                >
                Referral with the below details has been successfully verified,
                please proceed with adding the rest of the data.
                </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={12} sx={flexColumn} mt={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 8 }} sx={flexRow}>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Member Name:"}
                    value={`${getValue(
                      eligibilityResponse,
                      "response.record.subscriberName.first"
                    )} ${getValue(
                      eligibilityResponse,
                      "response.record.subscriberName.last"
                    )}`}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Date of birth:"}
                    value={formatDate(
                      getValue(eligibilityPayload, "dob"),
                      DateFormats.MM_DD_YYYY,
                      DateFormats.YYYY_MM_DD,
                    true)}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Subscriber Id:"}
                    value={getValue(eligibilityPayload, "policyId")}
                  />
                </Grid>
                <Grid size={{ xs: 2.5 }}>
                  <ReadOnlyField
                    label={"Health Plan:"}
                    value={getValue(eligibilityPayload, "payerName")}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} sx={flexRow}>
              <Grid size={{ xs: 4 }} sx={flexColumn}>
                {[
                  {
                    name: "street1",
                    label: "Street1: ",
                    helper: rules.street1,
                    searchInputRef: searchInputMember,
                    disabled: false,
                  },
                  {
                    name: "street2",
                    label: "Street2: ",
                    disabled: false,
                  },
                  {
                    name: "city",
                    label: "City: ",
                    helper: rules.city,
                    disabled: false,
                  },
                  {
                    name: "patientState",
                    label: "State: ",
                    helper: rules.state,
                    disabled: false,
                  },
                  {
                    name: "zipcode",
                    label: "Zip Code: ",
                    helper: rules.zipcode,
                    disabled: false,
                  },
                  {
                    name: "county",
                    label: "County: ",
                    disabled: false,
                  },
                ].map((field) => (
                  <Grid size={{ xs: 10 }} mt={2} key={field.name}>
                    <FormInputText
                      control={control}
                      name={field.name}
                      label={field.label}
                      helper={field.helper}
                      searchInputRef={field.searchInputRef}
                      disabled={field.disabled}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid size={{ xs: 4 }} sx={flexColumn}>
                <Grid size={{ xs: 8 }} mt={1}>
                  <ReadOnlyField label={"MBI:"} value={memberInfoData.mbi} />
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  {validatePhoneNumber(memberInfoData.phoneNumber) &&
                  getValue(
                    getValue(eligibilityResponse, "response"),
                    "supplementalInformation.info[0].homePhone", ""
                  ).length > 0 ? (
                    <ReadOnlyField
                      label={"Phone Number:"}
                      value={memberInfoData.phoneNumber}
                      mandatorySymbol={true}
                    />
                  ) : (
                    <PhoneFaxExtInput
                      label="Phone:"
                      control={control}
                      phoneFaxName={"phoneNumber"}
                      rules={rules.phoneNumber}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  <FormControl variant="standard" sx={width100}>
                    <FormInputDropdown
                      label={"Gender:"}
                      control={control}
                      name={"gender"}
                      list={GenderType}
                      helper={rules.genderRequired}
                      arrowDropDownIcon={false}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 8 }} mt={1}>
                  <FormControl variant="standard" sx={width100}>
                    <FormInputDropdown
                      label={"Member Preferred Language:"}
                      control={control}
                      name={`memberPreferredLanguage`}
                      list={getList(masterLanguageState, {
                        label: "languageName",
                        value: "languageId",
                      })}
                      disabled={disableLanguage}
                      helper={rules.languageRequired}
                      mandatorySymbol={true}
                      arrowDropDownIcon={false}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} mr={6} sx={flexRow}>
              <Grid size={{ xs: 12 }} sx={flexAllEnd}>
                <Button
                  variant="contained"
                  onClick={() => submit(1)}
                  sx={ButtonMinWidth}
                >
                  Create Request
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export type EligibilityCheckFailType = {
  eligibilityData: EligibilityCheckResponse;
  eligibilityPayload: EligibilityCheck | null | undefined;
  memberInfoDataState: MemberInfoData;
  saveMemberInfo: (payload: MemberInfoData) => void;
  resetAPIResponse: () => void;
  masterLanguageState: Language[];
};

export const EligibilityCheckFail = ({
  eligibilityPayload,
  saveMemberInfo,
  memberInfoDataState,
  eligibilityData,
  masterLanguageState
}: EligibilityCheckFailType) => {
  const methods = useForm<any>({
    defaultValues: "",
    mode: "all",
  });
  const { control, getValues, reset, handleSubmit } = methods;
  const formValues = useWatch({ control });
  const navigate = useNavigate();
  const searchInputMember = useRef() as MutableRefObject<HTMLInputElement>;
  const [memberInfoData, setMemberInfoData] = useState<MemberInfoData>(
    defaultMemberInfoData
  );

  useEffect(() => {
    initMapScript().then(() =>
      initAutocomplete(searchInputMember, setMemberInfoData, getValues, reset)
    );
    // eslint-disable-next-line
  }, [searchInputMember.current]);

  const onSubmit = () => {
    saveMemberInfo(memberInfoData);
  };

  const setMemberData = () => {
    const memberData = {
      dob: dayjs(getValue(eligibilityPayload, "dob")).format(
        DateFormats.MM_DD_YYYY
      ),
      gender: getValues().gender,
      mbi: getValues().mbi,
      subscriberId: getValue(eligibilityPayload, "policyId"),
      payerId: getValue(eligibilityPayload, "payerId"),
      lastName: getValue(eligibilityPayload, "lastName"),
      healthPlan: getValue(eligibilityPayload, "payerName").toUpperCase(),
      streetName1: getValues().street1,
      streetName2: getValues().street2,
      city: getValues().city,
      patientState: getValues().patientState,
      zipCode: getValues().zipcode,
      country: getValues().country,
      county: getValues().county,
      phoneNumber: getValues().phoneNumber,
      memberPreferredLanguage: getValues().memberPreferredLanguage,
      memberName: `${getValues().memberFirstName} ${getValues().memberLastName}`,
      residentialAddress: getResidentialAddress(getValues),
      mailingAddress: "",
      isIngest: true,
      isPatientCreation: false,
      groupId: getValue(eligibilityData, "response.supplementalInformation.info[0].userDefined7"),
    };
    updateMemberInfo(memberData, setMemberInfoData);
  };

  const submit = () => {
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    setMemberData();
  }, [formValues]);

  useEffect(() => {
    if (
      memberInfoDataState &&
      getValue(memberInfoDataState, "zipCode") != null &&
      getValue(memberInfoDataState, "zipCode", "").length > 0
    ) {
      navigate(ApplicationRoute.PRIOR_AUTH_WORKFLOW);
    }
  }, [memberInfoDataState]);

  return (
    <>
      <Grid size={12} m={2}>
        <Grid container>
          <Grid size={12} sx={{ display: "flex" }}>
            <FaExclamationCircleIcon />
            <Box sx={HeaderTitle}>
              <Typography
                variant="body1"
                color={colors.fonts[20]}
                fontWeight={fontWeight[4]}
              >
                {TextConstants.REFERRAL_NOT_ELIGIBLE}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={12} sx={flexColumn} mt={2}>
            <Grid size={{ xs: 8 }} sx={flexRow}>
              <Grid size={{ xs: 2.5 }}>
                <ReadOnlyField
                  label={"Date of birth:"}
                  value={formatDate(
                    getValue(eligibilityPayload, "dob"),
                    DateFormats.MM_DD_YYYY,
                    DateFormats.YYYY_MM_DD,
                    true
                  )}
                />
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <ReadOnlyField
                  label={"Subscriber Id:"}
                  value={getValue(eligibilityPayload, "policyId")}
                />
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <ReadOnlyField
                  label={"Health Plan:"}
                  value={getValue(eligibilityPayload, "payerName")}
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }} sx={flexRow}>
              <Grid size={{ xs: 4 }} sx={flexColumn} mt={2}>
                <Grid size={{ xs: 10 }} mt={2}>
                  <FormInputText
                    control={control}
                    helper={rules.eligibilityFirstNameTextRegex}
                    name={"memberFirstName"}
                    label="Member First Name: "
                  />
                </Grid>
                <Grid size={{ xs: 10 }} mt={2}>
                  <FormInputText
                    control={control}
                    helper={rules.eligibilityLastNameTextRegex}
                    name={"memberLastName"}
                    label="Member Last Name: "
                  />
                </Grid>
                <Grid size={{ xs: 10 }} mt={2}>
                  <PhoneFaxExtInput
                    label="Phone:"
                    control={control}
                    phoneFaxName={"phoneNumber"}
                    rules={rules.phoneNumber}
                  />
                </Grid>
                <Grid size={{ xs: 10 }} mt={2}>
                  <FormInputText
                    control={control}
                    helper={rules.mbiNumber}
                    name={"mbi"}
                    label="MBI: "
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 4 }} sx={flexColumn} mt={2}>
                {[
                  {
                  name: "street1",
                  label: "Street1: ",
                  helper: rules.street1,
                  searchInputRef: searchInputMember,
                  disabled: false,
                  },
                  {
                  name: "street2",
                  label: "Street2: ",
                  disabled: false,
                  },
                  {
                  name: "city",
                  label: "City: ",
                  helper: rules.city,
                  disabled: false,
                  },
                  {
                  name: "patientState",
                  label: "State: ",
                  helper: rules.state,
                  disabled: false,
                  },
                  {
                  name: "zipcode",
                  label: "Zipcode: ",
                  helper: rules.zipcode,
                  disabled: false,
                  },
                  {
                  name: "country",
                  label: "Country: ",
                  helper: rules.country,
                  disabled: false,
                  },
                ].map((field) => (
                  <Grid size={{ xs: 10 }} mt={2} key={field.name}>
                  <FormInputText
                    control={control}
                    name={field.name}
                    label={field.label}
                    helper={field.helper}
                    searchInputRef={field.searchInputRef}
                    disabled={field.disabled}
                  />
                  </Grid>
                ))}
              </Grid>
              <Grid size={{ xs: 4 }} sx={flexColumn} mt={2}>
                <Grid size={{ xs: 10 }} mt={2}>
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <FormInputDropdown
                      label={"Gender:"}
                      control={control}
                      name={"gender"}
                      list={GenderType}
                      helper={rules.genderRequired}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 10 }} mt={2}>
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <FormInputDropdown
                      label={"Member Preferred Language:"}
                      control={control}
                      name={`memberPreferredLanguage`}
                      list={getList(masterLanguageState, {
                        label: "languageName",
                        value: "languageId",
                      })}
                      helper={rules.languageRequired}
                      mandatorySymbol={true}
                      arrowDropDownIcon={false}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} mr={6} sx={flexRow}>
              <Grid size={{ xs: 12 }} sx={flexAllEnd}>
                <Button
                  variant="contained"
                  onClick={() => submit()}
                  sx={ButtonMinWidth}
                >
                  Enter Request Manually
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
